import { useContext, useEffect, useState } from "react";
import { BarChart, Bar, LabelList, Cell, YAxis } from "recharts";
import { defaultColorTheme } from "../../Constants/defaults";
import MachineDataContext from "../../themeContext/MachineDataContext";
import { BarChartItem, MachinesByGroup } from "../../Types/dashboardTypes";

import MyLegend from "./barChartLegend";

interface Props {
  colorTheme?: string[];
  selectedMachine?: MachinesByGroup;
  metricProps?: {
    quality: number;
    availability: number;
    performance: number;
  };
}

const MyBarChart: React.FC<Props> = (props) => {
  const { selectedMachine, metricProps } = props;
  const colorTheme = props.colorTheme ?? defaultColorTheme;
  const currentMachineData = useContext(MachineDataContext);
  const [metrics, setMetrics] = useState({
    quality: 0,
    availability: 0,
    performance: 0,
  });

  const data: BarChartItem[] = [
    {
      name: `${metrics.availability}%`,
      percentage: metrics.availability,
      legendName: "Availab.",
    },
    {
      name: `${metrics.performance}%`,
      percentage: metrics.performance,
      legendName: "Perform.",
    },
    {
      name: `${metrics.quality}%`,
      percentage: metrics.quality,
      legendName: "Quality",
    },
  ];

  useEffect(() => {
    // check to see if these metrics are coming in from a parent component first
    if (metricProps) {
      setMetrics(metricProps);
    } else {
      // if not, create them from the selected machine and machine data
      if (selectedMachine != undefined && currentMachineData[selectedMachine.machineSerial] != undefined) {
        const oeeQ = currentMachineData[selectedMachine.machineSerial].quality;
        const oeeQPercent = oeeQ ? Math.round((oeeQ * 10000) / 100) : 0;
        const oeeA = currentMachineData[selectedMachine.machineSerial].availability;
        const oeeAPercent = oeeA ? Math.round((oeeA * 10000) / 100) : 0;
        const oeeP = currentMachineData[selectedMachine.machineSerial].performance;
        const oeePPercent = oeeP ? Math.round((oeeP * 10000) / 100) : 0;

        setMetrics({ quality: oeeQPercent, availability: oeeAPercent, performance: oeePPercent });
      } else {
        // set to 0 if selected machine or machine data is not available
        setMetrics({
          quality: 0,
          availability: 0,
          performance: 0,
        });
      }
    }
  }, [currentMachineData, selectedMachine, metricProps]);

  return (
    <div className="my-bar-chart-component">
      <div className="bar-chart-title">OEE Breakdown</div>
      <BarChart className="bar-chart-machine-card" width={120} height={300} data={data}>
        <YAxis type="number" domain={[0, 100]} hide={true} />
        <Bar dataKey="percentage" radius={8} isAnimationActive={false} minPointSize={2}>
          <LabelList dataKey="name" position="insideBottom" angle={270} offset={25} fill="black" />
          {colorTheme.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={colorTheme[index]} />
          ))}
        </Bar>
      </BarChart>
      <MyLegend data={data} colorTheme={colorTheme} />
    </div>
  );
};

export default MyBarChart;
